import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Phone, FileText, ClipboardList, AlertCircle, CheckCircle,
  ArrowLeft, Copy, Clock, User, Stethoscope, MessageSquare, History,
  Database, Sparkles, Loader2, Mic, AlertTriangle, Activity
} from "lucide-react";
import { Link } from "react-router-dom";
import VoiceInput from "@/components/VoiceInput";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SBARPromptTool() {
  const [activeTab, setActiveTab] = useState("generate");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generatedPrompt, setGeneratedPrompt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recentCalls, setRecentCalls] = useState([]);
  const [sbarStats, setSbarStats] = useState(null);
  const [quickReference, setQuickReference] = useState(null);
  const [ehrSummary, setEhrSummary] = useState(null);
  const [loadingEhr, setLoadingEhr] = useState(false);
  const [voiceSbar, setVoiceSbar] = useState(null);
  const [vitalAlerts, setVitalAlerts] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    patientId: "",
    patientName: "",
    situationType: "",
    providerName: "",
    callerName: "",
    callerRole: "RN",
    useEhrAutofill: true  // AI-assisted EHR auto-fill enabled by default
  });
  
  // Call completion form
  const [callData, setCallData] = useState({
    providerResponse: "",
    ordersReceived: "",
    followUpRequired: false,
    followUpNotes: "",
    callDuration: ""
  });

  useEffect(() => {
    loadTemplates();
    loadRecentCalls();
    loadStats();
    loadQuickReference();
  }, []);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sbar/templates`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error("Failed to load templates:", err);
    }
  };

  const loadRecentCalls = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sbar/recent-calls?limit=10`);
      const data = await res.json();
      setRecentCalls(data.calls || []);
    } catch (err) {
      console.error("Failed to load recent calls:", err);
    }
  };

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sbar/stats`);
      const data = await res.json();
      setSbarStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  const loadQuickReference = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sbar/quick-reference`);
      const data = await res.json();
      setQuickReference(data);
    } catch (err) {
      console.error("Failed to load quick reference:", err);
    }
  };

  const loadTemplateDetails = async (templateKey) => {
    try {
      const res = await fetch(`${API_URL}/api/sbar/templates/${templateKey}`);
      const data = await res.json();
      setSelectedTemplate(data);
    } catch (err) {
      toast.error("Failed to load template details");
    }
  };

  const fetchEhrSummary = async (patientId) => {
    if (!patientId) return;
    
    setLoadingEhr(true);
    try {
      const res = await fetch(`${API_URL}/api/sbar/patient/${patientId}/ehr-summary`);
      const data = await res.json();
      setEhrSummary(data);
      
      if (data.found && data.patient_name) {
        // Auto-fill patient name from EHR
        setFormData(prev => ({
          ...prev,
          patientName: data.patient_name
        }));
        toast.success("Patient EHR data found - fields will be auto-filled");
      } else {
        toast.info("No EHR data found - manual entry required");
      }
    } catch (err) {
      console.error("Failed to fetch EHR summary:", err);
      setEhrSummary(null);
    }
    setLoadingEhr(false);
  };

  const generatePrompt = async () => {
    if (!formData.patientId || !formData.patientName || !formData.situationType) {
      toast.error("Please fill in patient ID, name, and select a situation type");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sbar/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          patient_id: formData.patientId,
          patient_name: formData.patientName,
          situation_type: formData.situationType,
          provider_name: formData.providerName,
          caller_name: formData.callerName,
          caller_role: formData.callerRole,
          use_ehr_autofill: formData.useEhrAutofill
        })
      });
      const data = await res.json();
      setGeneratedPrompt(data);
      
      if (data.autofilled) {
        toast.success("SBAR generated with EHR auto-fill applied");
      } else {
        toast.success("SBAR prompt generated successfully");
      }
    } catch (err) {
      toast.error("Failed to generate prompt");
    }
    setLoading(false);
  };

  const handleVoiceToSbar = async (transcription) => {
    if (!formData.patientId || !formData.patientName) {
      toast.error("Please enter patient ID and name first");
      return;
    }

    setLoading(true);
    try {
      // Convert transcription to base64 audio for the API
      // Since we already have the text, we'll use a simplified approach
      const res = await fetch(`${API_URL}/api/voice-sbar/analyze-vitals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pain_score: extractVitalFromText(transcription.text, "pain"),
          oxygen_saturation: extractVitalFromText(transcription.text, "oxygen"),
          respiratory_rate: extractVitalFromText(transcription.text, "respiratory"),
          heart_rate: extractVitalFromText(transcription.text, "heart")
        })
      });
      
      const alertsData = await res.json();
      setVitalAlerts(alertsData.alerts || []);
      
      // Set voice SBAR data with the transcription
      setVoiceSbar({
        transcription: transcription.text,
        situation_type: formData.situationType || "change_in_condition",
        has_alerts: alertsData.has_critical
      });
      
      if (alertsData.has_critical) {
        toast.warning("Critical vital signs detected - review alerts");
      } else {
        toast.success("Voice transcription complete");
      }
    } catch (err) {
      // Fallback - just show the transcription
      setVoiceSbar({
        transcription: transcription.text,
        situation_type: formData.situationType || "change_in_condition",
        has_alerts: false
      });
      toast.success("Voice transcription complete");
    }
    setLoading(false);
  };

  const extractVitalFromText = (text, type) => {
    const lowerText = text.toLowerCase();
    const patterns = {
      pain: /pain\s*(?:level|score|is)?\s*(?:of|:)?\s*(\d+)/i,
      oxygen: /(?:o2|oxygen|spo2|sat)\s*(?:level|is|at|:)?\s*(\d+)/i,
      respiratory: /(?:respiratory|rr|breathing)\s*(?:rate|is|at|:)?\s*(\d+)/i,
      heart: /(?:heart rate|hr|pulse)\s*(?:is|at|of|:)?\s*(\d+)/i
    };
    
    const match = text.match(patterns[type]);
    return match ? parseInt(match[1]) : null;
  };

  const logCompletedCall = async () => {
    if (!generatedPrompt) {
      toast.error("Please generate a prompt first");
      return;
    }

    setLoading(true);
    try {
      const ordersArray = callData.ordersReceived
        ? callData.ordersReceived.split("\n").filter(o => o.trim())
        : [];

      const res = await fetch(`${API_URL}/api/sbar/complete-call`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sbar_prompt_id: generatedPrompt.id,
          patient_id: formData.patientId,
          patient_name: formData.patientName,
          situation_type: formData.situationType,
          provider_name: formData.providerName || "Unknown Provider",
          provider_response: callData.providerResponse,
          orders_received: ordersArray,
          follow_up_required: callData.followUpRequired,
          follow_up_notes: callData.followUpNotes,
          caller_name: formData.callerName || "Staff Member",
          caller_role: formData.callerRole,
          call_duration_minutes: callData.callDuration ? parseInt(callData.callDuration) : null
        })
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success("Call logged successfully");
        // Reset form
        setGeneratedPrompt(null);
        setCallData({
          providerResponse: "",
          ordersReceived: "",
          followUpRequired: false,
          followUpNotes: "",
          callDuration: ""
        });
        loadRecentCalls();
        loadStats();
      } else {
        throw new Error("Failed to log call");
      }
    } catch (err) {
      toast.error("Failed to log call");
    }
    setLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const formatSBARForCopy = () => {
    if (!generatedPrompt) return "";
    return `SBAR Communication - ${formData.patientName}
═══════════════════════════════════════

📍 SITUATION
${generatedPrompt.situation.map(s => `• ${s}`).join("\n")}

📋 BACKGROUND
${generatedPrompt.background.map(b => `• ${b}`).join("\n")}

🔍 ASSESSMENT
${generatedPrompt.assessment.map(a => `• ${a}`).join("\n")}

💊 RECOMMENDATION
${generatedPrompt.recommendation.map(r => `• ${r}`).join("\n")}

═══════════════════════════════════════
Generated: ${new Date(generatedPrompt.generated_at).toLocaleString()}
Caller: ${formData.callerName || "Staff"} (${formData.callerRole})
Provider: ${formData.providerName || "To be called"}`;
  };

  const situationTypeColors = {
    pain_management: "bg-red-100 text-red-800 border-red-200",
    respiratory_distress: "bg-orange-100 text-orange-800 border-orange-200",
    nausea_vomiting: "bg-yellow-100 text-yellow-800 border-yellow-200",
    agitation_delirium: "bg-purple-100 text-purple-800 border-purple-200",
    change_in_condition: "bg-blue-100 text-blue-800 border-blue-200",
    end_of_life: "bg-slate-100 text-slate-800 border-slate-200",
    medication_question: "bg-green-100 text-green-800 border-green-200",
    family_concern: "bg-teal-100 text-teal-800 border-teal-200"
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-6" data-testid="sbar-prompt-tool">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/clinical-ai" className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">SBAR Communication Tool</h1>
              <p className="text-slate-600">Structured prompts for clinician-provider calls</p>
            </div>
          </div>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
            <Phone className="w-3 h-3 mr-1" />
            Provider Communication
          </Badge>
        </div>

        {/* Stats Cards */}
        {sbarStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Total Calls</p>
                    <p className="text-2xl font-bold text-blue-900">{sbarStats.total_calls}</p>
                  </div>
                  <Phone className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-amber-600">Need Follow-up</p>
                    <p className="text-2xl font-bold text-amber-900">{sbarStats.calls_requiring_followup}</p>
                  </div>
                  <AlertCircle className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Avg Duration</p>
                    <p className="text-2xl font-bold text-green-900">{sbarStats.average_call_duration_minutes} min</p>
                  </div>
                  <Clock className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Templates</p>
                    <p className="text-2xl font-bold text-purple-900">{templates.length}</p>
                  </div>
                  <FileText className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger value="generate" className="flex items-center gap-2" data-testid="generate-tab">
              <ClipboardList className="w-4 h-4" />
              Generate SBAR
            </TabsTrigger>
            <TabsTrigger value="voice" className="flex items-center gap-2" data-testid="voice-tab">
              <Mic className="w-4 h-4" />
              Voice-to-SBAR
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2" data-testid="history-tab">
              <History className="w-4 h-4" />
              Call History
            </TabsTrigger>
            <TabsTrigger value="reference" className="flex items-center gap-2" data-testid="reference-tab">
              <FileText className="w-4 h-4" />
              Quick Reference
            </TabsTrigger>
          </TabsList>

          {/* Generate SBAR Tab */}
          <TabsContent value="generate" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Form */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-blue-600" />
                    Generate SBAR Prompt
                  </CardTitle>
                  <CardDescription>
                    Fill in patient details and select a situation type
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Patient ID *</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.patientId}
                          onChange={(e) => setFormData({...formData, patientId: e.target.value})}
                          placeholder="Enter patient ID"
                          data-testid="patient-id-input"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => fetchEhrSummary(formData.patientId)}
                          disabled={!formData.patientId || loadingEhr}
                          title="Fetch EHR data"
                        >
                          {loadingEhr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Patient Name *</Label>
                      <Input
                        value={formData.patientName}
                        onChange={(e) => setFormData({...formData, patientName: e.target.value})}
                        placeholder="Enter patient name"
                        data-testid="patient-name-input"
                      />
                    </div>
                  </div>

                  {/* AI-Assisted EHR Auto-fill Toggle */}
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-violet-600" />
                      <span className="text-sm font-medium text-violet-800">AI-Assisted EHR Auto-fill</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="ehr-autofill"
                        checked={formData.useEhrAutofill}
                        onCheckedChange={(checked) => setFormData({...formData, useEhrAutofill: checked})}
                      />
                      <label htmlFor="ehr-autofill" className="text-xs text-violet-600">
                        Auto-fill from patient records
                      </label>
                    </div>
                  </div>

                  {/* EHR Summary Preview */}
                  {ehrSummary && ehrSummary.found && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-green-800">EHR Data Found</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-green-700">
                        <div><strong>Diagnosis:</strong> {ehrSummary.diagnosis}</div>
                        <div><strong>Code Status:</strong> {ehrSummary.code_status}</div>
                        <div><strong>Medications:</strong> {ehrSummary.medications?.length || 0} active</div>
                        <div><strong>Allergies:</strong> {ehrSummary.allergies?.length || 0} recorded</div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Situation Type *</Label>
                    <Select 
                      value={formData.situationType} 
                      onValueChange={(v) => {
                        setFormData({...formData, situationType: v});
                        loadTemplateDetails(v);
                      }}
                    >
                      <SelectTrigger data-testid="situation-type-select">
                        <SelectValue placeholder="Select situation type" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.key} value={t.key}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Provider Name</Label>
                      <Input
                        value={formData.providerName}
                        onChange={(e) => setFormData({...formData, providerName: e.target.value})}
                        placeholder="Dr. Smith"
                      />
                    </div>
                    <div>
                      <Label>Your Name</Label>
                      <Input
                        value={formData.callerName}
                        onChange={(e) => setFormData({...formData, callerName: e.target.value})}
                        placeholder="Your name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Your Role</Label>
                    <Select value={formData.callerRole} onValueChange={(v) => setFormData({...formData, callerRole: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="RN">Registered Nurse (RN)</SelectItem>
                        <SelectItem value="LPN">Licensed Practical Nurse (LPN)</SelectItem>
                        <SelectItem value="CNA">Certified Nursing Assistant (CNA)</SelectItem>
                        <SelectItem value="SW">Social Worker (SW)</SelectItem>
                        <SelectItem value="Chaplain">Chaplain</SelectItem>
                        <SelectItem value="Case Manager">Case Manager</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={generatePrompt}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="generate-btn"
                  >
                    {loading ? "Generating..." : "Generate SBAR Prompt"}
                  </Button>
                </CardContent>
              </Card>

              {/* Generated Prompt */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CardTitle>Generated SBAR Prompt</CardTitle>
                      {generatedPrompt?.autofilled && (
                        <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                          <Sparkles className="w-3 h-3 mr-1" />
                          EHR Auto-filled
                        </Badge>
                      )}
                    </div>
                    {generatedPrompt && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(formatSBARForCopy())}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {generatedPrompt ? (
                    <div className="space-y-4">
                      {/* Situation */}
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-red-800 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            S - SITUATION
                          </h3>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(generatedPrompt.situation.join("\n"))}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {generatedPrompt.situation.map((s, i) => (
                            <li key={i} className="text-red-700">• {s}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Background */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            B - BACKGROUND
                          </h3>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(generatedPrompt.background.join("\n"))}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {generatedPrompt.background.map((b, i) => (
                            <li key={i} className="text-blue-700">• {b}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Assessment */}
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                            <Stethoscope className="w-4 h-4" />
                            A - ASSESSMENT
                          </h3>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(generatedPrompt.assessment.join("\n"))}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {generatedPrompt.assessment.map((a, i) => (
                            <li key={i} className="text-amber-700">• {a}</li>
                          ))}
                        </ul>
                      </div>

                      {/* Recommendation */}
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-green-800 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            R - RECOMMENDATION
                          </h3>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(generatedPrompt.recommendation.join("\n"))}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                        <ul className="space-y-1 text-sm">
                          {generatedPrompt.recommendation.map((r, i) => (
                            <li key={i} className="text-green-700">• {r}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-slate-500 py-12">
                      <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Fill in the form and click "Generate SBAR Prompt"</p>
                      <p className="text-sm mt-2">Your structured communication guide will appear here</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Log Call Section */}
            {generatedPrompt && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Log Completed Call
                  </CardTitle>
                  <CardDescription>
                    Document the outcome of your provider communication
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Label>Provider Response</Label>
                        <Textarea
                          value={callData.providerResponse}
                          onChange={(e) => setCallData({...callData, providerResponse: e.target.value})}
                          placeholder="Document the provider's response..."
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>Orders Received (one per line)</Label>
                        <Textarea
                          value={callData.ordersReceived}
                          onChange={(e) => setCallData({...callData, ordersReceived: e.target.value})}
                          placeholder="Morphine 4mg PO q4h PRN&#10;Ondansetron 4mg SL PRN&#10;..."
                          rows={3}
                        />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label>Call Duration (minutes)</Label>
                        <Input
                          type="number"
                          value={callData.callDuration}
                          onChange={(e) => setCallData({...callData, callDuration: e.target.value})}
                          placeholder="5"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="followup"
                          checked={callData.followUpRequired}
                          onCheckedChange={(checked) => setCallData({...callData, followUpRequired: checked})}
                        />
                        <label htmlFor="followup" className="text-sm font-medium">
                          Follow-up required
                        </label>
                      </div>
                      {callData.followUpRequired && (
                        <div>
                          <Label>Follow-up Notes</Label>
                          <Textarea
                            value={callData.followUpNotes}
                            onChange={(e) => setCallData({...callData, followUpNotes: e.target.value})}
                            placeholder="Document follow-up requirements..."
                            rows={2}
                          />
                        </div>
                      )}
                      <Button 
                        onClick={logCompletedCall}
                        disabled={loading}
                        className="w-full bg-green-600 hover:bg-green-700"
                        data-testid="log-call-btn"
                      >
                        {loading ? "Logging..." : "Log Completed Call"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Voice-to-SBAR Tab */}
          <TabsContent value="voice" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mic className="w-5 h-5 text-blue-600" />
                  Voice-to-SBAR
                </CardTitle>
                <CardDescription>
                  Dictate your SBAR call and convert it to structured documentation with vital alerts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Patient ID *</Label>
                    <Input
                      value={formData.patientId}
                      onChange={(e) => setFormData({...formData, patientId: e.target.value})}
                      placeholder="Enter patient ID"
                    />
                  </div>
                  <div>
                    <Label>Patient Name *</Label>
                    <Input
                      value={formData.patientName}
                      onChange={(e) => setFormData({...formData, patientName: e.target.value})}
                      placeholder="Enter patient name"
                    />
                  </div>
                </div>

                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg text-center">
                  <Mic className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                  <p className="text-blue-700 mb-2 font-medium">Click to start recording your SBAR call</p>
                  <p className="text-sm text-blue-600 mb-4">
                    Example: "I'm calling about Mrs. Johnson who has pain level 8 out of 10. Her current morphine isn't controlling the pain..."
                  </p>
                  <VoiceInput
                    onTranscription={handleVoiceToSbar}
                    context="sbar_call"
                    enhanced={false}
                    patientId={formData.patientId}
                    patientName={formData.patientName}
                    placeholder="Start Voice Recording"
                    buttonVariant="default"
                    size="lg"
                    className="justify-center"
                  />
                </div>

                {/* Vital Alerts */}
                {vitalAlerts.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Vital Sign Alerts
                    </h4>
                    <div className="space-y-2">
                      {vitalAlerts.map((alert, idx) => (
                        <div key={idx} className={`p-3 rounded ${
                          alert.type === "critical" ? "bg-red-100 border border-red-300" : "bg-amber-100 border border-amber-300"
                        }`}>
                          <div className="flex items-start justify-between">
                            <div>
                              <p className={`font-medium ${alert.type === "critical" ? "text-red-800" : "text-amber-800"}`}>
                                {alert.message}
                              </p>
                              <p className="text-sm text-slate-600 mt-1">{alert.recommendation}</p>
                            </div>
                            <Badge variant={alert.type === "critical" ? "destructive" : "warning"}>
                              {alert.vital}: {alert.value}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Voice Transcription Result */}
                {voiceSbar && (
                  <div className="space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Voice Transcription
                      </h4>
                      <p className="text-sm text-slate-700">{voiceSbar.transcription}</p>
                    </div>

                    <div className="flex gap-3">
                      <Button 
                        onClick={() => {
                          // Use the transcription to populate SBAR form
                          setFormData(prev => ({
                            ...prev,
                            situationType: voiceSbar.situation_type
                          }));
                          setActiveTab("generate");
                          toast.success("Transcription applied - generate SBAR to continue");
                        }}
                        className="bg-violet-600 hover:bg-violet-700"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Convert to Structured SBAR
                      </Button>
                      <Button variant="outline" onClick={() => copyToClipboard(voiceSbar.transcription)}>
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Transcription
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-slate-600" />
                  Recent SBAR Calls
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentCalls.length > 0 ? (
                  <div className="space-y-4">
                    {recentCalls.map((call, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold">{call.patient_name}</span>
                              <Badge className={situationTypeColors[call.situation_type] || "bg-slate-100"}>
                                {call.situation_type?.replace(/_/g, " ")}
                              </Badge>
                              {call.follow_up_required && (
                                <Badge variant="destructive" className="text-xs">
                                  Follow-up needed
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-slate-600 space-y-1">
                              <p><User className="w-3 h-3 inline mr-1" /> Called by: {call.caller_name} ({call.caller_role})</p>
                              <p><Stethoscope className="w-3 h-3 inline mr-1" /> Provider: {call.provider_name}</p>
                              {call.orders_received?.length > 0 && (
                                <p><FileText className="w-3 h-3 inline mr-1" /> Orders: {call.orders_received.join(", ")}</p>
                              )}
                            </div>
                          </div>
                          <div className="text-right text-sm text-slate-500">
                            <p>{new Date(call.completed_at).toLocaleDateString()}</p>
                            <p>{new Date(call.completed_at).toLocaleTimeString()}</p>
                            {call.call_duration_minutes && (
                              <p className="flex items-center justify-end gap-1">
                                <Clock className="w-3 h-3" />
                                {call.call_duration_minutes} min
                              </p>
                            )}
                          </div>
                        </div>
                        {call.provider_response && (
                          <div className="mt-3 p-2 bg-slate-50 rounded text-sm">
                            <strong>Response:</strong> {call.provider_response}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-slate-500 py-12">
                    <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No calls logged yet</p>
                    <p className="text-sm mt-2">Completed calls will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quick Reference Tab */}
          <TabsContent value="reference" className="space-y-6">
            {quickReference && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>{quickReference.title}</CardTitle>
                    <CardDescription>{quickReference.overview}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {Object.entries(quickReference.components).map(([key, comp]) => (
                      <div key={key} className="p-4 border rounded-lg">
                        <h3 className="font-semibold text-lg mb-2">{key}</h3>
                        <p className="text-sm text-slate-600 mb-3">{comp.description}</p>
                        <ul className="space-y-1">
                          {comp.tips.map((tip, i) => (
                            <li key={i} className="text-sm flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Best Practices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {quickReference.best_practices.map((practice, i) => (
                        <li key={i} className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <span className="text-blue-800">{practice}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Available SBAR Templates</CardTitle>
                    <CardDescription>Select a template to view situation-specific prompts</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {templates.map((t) => (
                        <Button
                          key={t.key}
                          variant="outline"
                          className={`h-auto py-4 flex flex-col items-center gap-2 ${situationTypeColors[t.key] || ""}`}
                          onClick={() => {
                            setFormData({...formData, situationType: t.key});
                            loadTemplateDetails(t.key);
                            setActiveTab("generate");
                          }}
                        >
                          <FileText className="w-6 h-6" />
                          <span className="text-sm font-medium text-center">{t.name}</span>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
